import api from './api';

// =====================================================
// RESOURCES & KNOWLEDGE BASE SERVICE
// TMOS Platform - Frontend Service
// =====================================================
// Service: resourcesService
// Description: Frontend service for Resources & Knowledge Base System
// Author: TMOS Development Team
// Date: September 30, 2025
// =====================================================

export interface Resource {
  id: string;
  tenant_id: string;
  title: string;
  description?: string;
  url?: string;
  file_path?: string;
  resource_type: 'link' | 'document';
  image_url?: string;
  auto_extracted: boolean;
  active_from: string;
  active_until?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  featured_in_widget?: boolean;
  is_active?: boolean;
  file_name?: string;
  file_size?: number;
  created_by_name?: string;
  tags?: ResourceTag[];
  access_count?: number;
  last_accessed?: string;
}

export interface ResourceTag {
  id: string;
  name: string;
  description?: string;
  is_predefined: boolean;
}

export interface CreateResourceData {
  title: string;
  description?: string;
  url?: string;
  file_path?: string;
  resource_type: 'link' | 'document';
  active_from?: string;
  active_until?: string;
  tag_ids?: string[];
}

export interface UpdateResourceData extends Partial<CreateResourceData> {
  featured_in_widget?: boolean;
  is_active?: boolean;
}

export interface ResourceFilter {
  resource_type?: 'link' | 'document';
  tag_ids?: string[];
  search_term?: string;
  active_only?: boolean;
  limit?: number;
  offset?: number;
}

export interface ResourceSearchResponse {
  resources: Resource[];
  total_count: number;
  limit: number;
  offset: number;
}

export interface ResourceAccessLog {
  id: string;
  resource_id: string;
  user_id: string;
  action: string;
  ip_address?: string;
  user_agent?: string;
  user_name?: string;
  access_timestamp: string;
  created_at: string;
}

export interface FileUploadResponse {
  file_path: string;
  original_name: string;
  size: number;
  mime_type: string;
}

export const resourcesService = {
  // =====================================================
  // RESOURCE MANAGEMENT
  // =====================================================

  // Get all resources
  async getResources(filters?: ResourceFilter): Promise<ResourceSearchResponse> {
    const params = new URLSearchParams();
    
    if (filters?.resource_type) {
      params.append('resource_type', filters.resource_type);
    }
    if (filters?.tag_ids && filters.tag_ids.length > 0) {
      params.append('tag_ids', filters.tag_ids.join(','));
    }
    if (filters?.search_term) {
      params.append('search', filters.search_term);
    }
    if (filters?.active_only !== undefined) {
      params.append('active_only', filters.active_only.toString());
    }
    if (filters?.limit) {
      params.append('limit', filters.limit.toString());
    }
    if (filters?.offset) {
      params.append('offset', filters.offset.toString());
    }

    const queryString = params.toString();
    const url = queryString ? `/resources?${queryString}` : '/resources';
    
    const response = await api.get(url);
    return response.data.data;
  },

  // Get widget resources (smart approach)
  async getWidgetResources(mode: 'recent' | 'featured' | 'smart' = 'smart', limit: number = 5): Promise<Resource[]> {
    const params = new URLSearchParams();
    params.append('mode', mode);
    params.append('limit', limit.toString());

    const response = await api.get(`/resources/widget?${params.toString()}`);
    return response.data.data.resources;
  },

  // Get a single resource
  async getResource(id: string): Promise<Resource> {
    const response = await api.get(`/resources/${id}`);
    return response.data.data;
  },

  // Create a new resource
  async createResource(data: CreateResourceData): Promise<Resource> {
    const response = await api.post('/resources', data);
    return response.data.data;
  },

  // Update a resource
  async updateResource(id: string, data: UpdateResourceData): Promise<Resource> {
    const response = await api.put(`/resources/${id}`, data);
    return response.data.data;
  },

  // Delete a resource
  async deleteResource(id: string): Promise<void> {
    await api.delete(`/resources/${id}`);
  },

  // =====================================================
  // FILE UPLOAD
  // =====================================================

  // Upload a file
  async uploadFile(file: File): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/resources/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data.data;
  },

  // =====================================================
  // TAG MANAGEMENT
  // =====================================================

  // Get all tags
  async getTags(): Promise<ResourceTag[]> {
    const response = await api.get('/resources/tags/all');
    return response.data.data;
  },

  // Create a new tag
  async createTag(name: string, description?: string): Promise<ResourceTag> {
    const response = await api.post('/resources/tags', { name, description });
    return response.data.data;
  },

  // =====================================================
  // ACCESS LOGGING
  // =====================================================

  // Get resource access logs
  async getResourceLogs(resourceId: string, limit?: number): Promise<ResourceAccessLog[]> {
    const params = limit ? `?limit=${limit}` : '';
    const response = await api.get(`/resources/${resourceId}/logs${params}`);
    return response.data.data;
  },

  // Alias for getResourceLogs
  async getAccessLogs(resourceId: string, limit?: number): Promise<ResourceAccessLog[]> {
    return this.getResourceLogs(resourceId, limit);
  },

  // Log resource access
  async logAccess(resourceId: string, action: string): Promise<void> {
    await api.post(`/resources/${resourceId}/access`, { action });
  },

  // =====================================================
  // UTILITY METHODS
  // =====================================================

  // Get resource URL for external links
  getResourceUrl(resource: Resource): string {
    if (resource.resource_type === 'link' && resource.url) {
      return resource.url;
    }
    if (resource.resource_type === 'document' && resource.file_path) {
      // In a real implementation, this would be a proper file serving URL
      return `/api/v1/resources/files/${resource.id}`;
    }
    return '#';
  },

  // Check if resource is active
  isResourceActive(resource: Resource): boolean {
    const now = new Date();
    const activeFrom = new Date(resource.active_from);
    const activeUntil = resource.active_until ? new Date(resource.active_until) : null;
    
    return now >= activeFrom && (!activeUntil || now <= activeUntil);
  },

  // Get resource type icon
  getResourceTypeIcon(resource: Resource): string {
    if (resource.resource_type === 'link') {
      return '🔗';
    } else if (resource.resource_type === 'document') {
      const filePath = resource.file_path || '';
      if (filePath.endsWith('.pdf')) return '📄';
      if (filePath.endsWith('.docx') || filePath.endsWith('.doc')) return '📝';
      if (filePath.endsWith('.xlsx') || filePath.endsWith('.xls')) return '📊';
      if (filePath.endsWith('.pptx') || filePath.endsWith('.ppt')) return '📊';
      return '📄';
    }
    return '📄';
  },

  // Get resource type label
  getResourceTypeLabel(resource: Resource): string {
    if (resource.resource_type === 'link') {
      return 'External Link';
    } else if (resource.resource_type === 'document') {
      const filePath = resource.file_path || '';
      const extension = filePath.substring(filePath.lastIndexOf('.') + 1).toUpperCase();
      return `${extension} Document`;
    }
    return 'Resource';
  },


  // Format file size
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  // Get security level from tags
  getSecurityLevel(resource: Resource): 'public' | 'internal' | 'confidential' | 'sensitive' {
    if (!resource.tags) return 'public';
    
    const securityTags = resource.tags.filter(tag => 
      ['Position of Trust', 'Confidential', 'Sensitive'].includes(tag.name)
    );
    
    if (securityTags.some(tag => tag.name === 'Position of Trust')) return 'sensitive';
    if (securityTags.some(tag => tag.name === 'Confidential')) return 'confidential';
    if (securityTags.some(tag => tag.name === 'Sensitive')) return 'internal';
    
    return 'public';
  },

  // Get security level color
  getSecurityLevelColor(level: 'public' | 'internal' | 'confidential' | 'sensitive'): string {
    switch (level) {
      case 'public': return 'text-green-600';
      case 'internal': return 'text-blue-600';
      case 'confidential': return 'text-orange-600';
      case 'sensitive': return 'text-red-600';
      default: return 'text-gray-600';
    }
  }
};
